/* eslint-disable react-hooks/exhaustive-deps */
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  Button,
  Checkbox,
  Divider,
  Flex,
  Switch,
  Space,
  Table,
  TableHeader,
  TableRow,
  TableCell,
  TableBody,
  Text,
  useColorMode,
  useColorStyle,
  Stack,
  LinkButton,
  Tag,
} from '@tonic-one/react';

import React, { useEffect, useState, useMemo, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useAccountStore } from '../../../models/useAccountModel';
import { useOrgStore } from '../../../models/useOrgModel';
import { IS_MEA } from '../../../utils/const';
import { AdministrationApi, ProvisionApi } from '@Apis';

import { useDisclosure } from '../../../components/Common/Tooltip/TextEllipsis/utils';
import ErrorAlert from '../../../components/Common/ErrorAlert';
import CSpinner from '../../../components/CSpinner';
import SmartTooltip from '../../Report/SmartTooltip';
import {
  EuqSetDataList,
  EuqThreatTypeItem,
  EuqSettingsList,
} from '../../../types/administrator';
import { Info } from '../../../components/Common/Info';
import { PolicyType } from '../../../types/app';
import SaveModal from './SaveModal';
import { normalizeThreatTypes, buildThreatTypeItems } from './utils';
import { PermissionNameEnum } from '@Types/permissions';
import { usePermissionStore } from '@Models/usePermissionModel';
import Organization from '@Components/Common/Organization';
import { handleNavigate } from '@Utils/common';
import { useNavigate } from 'react-router-dom';

export interface AddInPanelProps {
  policyTypes: PolicyType[];
  descriptionContent: ReactNode;
  notProvisionKey?: string;
  apiCategory?: string;
  infoBanner?: ReactNode;
  forceDisable?: boolean;
}

const AddInPanel = ({
  policyTypes,
  descriptionContent,
  notProvisionKey = 'administration.addIn.quarantineManagement.notProvision',
  apiCategory,
  infoBanner,
  forceDisable = false,
}: AddInPanelProps) => {
  const navigate = useNavigate();
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const [loading, setLoading] = useState<boolean>(false);
  const { t } = useTranslation();
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined);

  const [items, setItems] = useState<EuqSetDataList[] | []>([]);
  const [enableViewList, setViewEnableList] = useState<EuqThreatTypeItem>();
  const { groupCompanyGuid } = useAccountStore();
  const { switchOrgId, orgId, defaultSingleOrgId } = useOrgStore();
  if (defaultSingleOrgId) {
    switchOrgId(defaultSingleOrgId);
  }
  const addInOrgId = orgId === 'all' ? groupCompanyGuid : orgId;
  const [rowSelection, setRowSelection] = useState({});
  const [updateIndex, setUpdateIndex] = useState<Set<number>>(
    new Set<number>()
  );
  const [updateActionIndex, setUpdateActionIndex] = useState<Set<number>>(
    new Set<number>()
  );

  const [approvedSenderEnable, setApprovedSenderEnable] =
    useState<boolean>(false);
  const [enableStatus, setEnableStatus] = useState<boolean>(false);
  const isDisabled = !enableStatus || forceDisable;
  const [feedbackEnableStatus, setFeedbackEnableStatus] =
    useState<boolean>(false);
  const [feedbackManualEnableStatus, setFeedbackManualEnableStatus] =
    useState<boolean>(false);
  const [
    feedbackSkipOnApprovedHeaderEnableStatus,
    setFeedbackSkipOnApprovedHeaderEnableStatus,
  ] = useState(false);
  const [feedbackToAdminEnableStatus, setFeedbackToAdminEnableStatus] =
    useState<boolean>(false);
  const [feedbackToAdminMailBoxes, setFeedbackToAdminMailBoxes] = useState<
    string[]
  >([]);
  const [
    feedbackPhishingSpamAutoRemediationEnableStatus,
    setFeedbackPhishingSpamAutoRemediationEnableStatus,
  ] = useState(false);
  const [isShowMultiService, setIsShowMultiService] =
    React.useState<boolean>(false);
  const [moveToJunkFolderEnableStatus, setMoveToJunkFolderEnableStatus] =
    useState<boolean>(false);
  const [confirmationMailEnableStatus, setConfirmationMailEnableStatus] =
    useState(false);
  const [aiChatEnableStatus, setAiChatEnableStatus] = useState<boolean>(false);

  const [saveLoading, setSaveLoading] = useState(false);
  const [saveModalTitle, setSaveModalTitle] = useState('Save Settings');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [saveModalContent, setSaveModalContent] = useState<ReactNode>();
  const [customizedMailEnabled, setCustomizedMailEnabled] = useState(0);
  const [customizedSubject, setCustomizedSubject] = useState('');
  const [messageContent, setMessageContent] = useState('');
  async function loadData() {
    try {
      setLoading(true);
      setErrorMsg('');
      const [euqRes, provisionRes] = await Promise.all([
        AdministrationApi.getEuqSettings(undefined, apiCategory),
        ProvisionApi.getProvisionedServicesByOrgId(orgId),
      ]);
      const data: any = euqRes.data;
      const provisionedTypes = provisionRes.data;
      const result = provisionedTypes.filter((item) =>
        policyTypes.includes(item)
      );
      if (result && result.length > 0) {
        setIsShowMultiService(true);
        // Normalize the threat_type map so every known type is present with
        // numeric defaults. A platform whose euqSettings response omits some
        // types (e.g. Gmail) would otherwise throw here and downstream in
        // onSave/confirmSave, dropping the panel into the generic error state.
        const normalizedThreatType = normalizeThreatTypes(
          data?.euq_assistant?.threat_type
        );
        const dataList = buildThreatTypeItems(normalizedThreatType);
        setEnableStatus(Boolean(data?.euq_assistant?.isEnabled));
        setApprovedSenderEnable(
          Boolean(data?.euq_assistant?.approved_sender?.enabled)
        );
        setFeedbackEnableStatus(Boolean(data?.euq_feedback?.isEnabled));
        setFeedbackManualEnableStatus(
          Boolean(data?.euq_feedback_manual_approve?.isEnabled)
        );
        setFeedbackSkipOnApprovedHeaderEnableStatus(
          Boolean(data?.euq_feedback_skip_on_approved_header?.isEnabled)
        );
        setFeedbackToAdminEnableStatus(
          Boolean(data?.euq_feedback_to_admin?.isEnabled)
        );
        setFeedbackToAdminMailBoxes(
          data?.euq_feedback_to_admin?.euq_feedback_mail_boxes ?? []
        );
        setFeedbackPhishingSpamAutoRemediationEnableStatus(
          Boolean(data?.euq_feedback_phishing_spam_auto_remediation?.isEnabled)
        );
        setViewEnableList(normalizedThreatType);
        setItems(dataList);
        setUpdateIndex(new Set());
        setUpdateActionIndex(new Set());
        setMoveToJunkFolderEnableStatus(
          Boolean(data?.euq_move_to_junk_folder?.isEnabled)
        );
        setConfirmationMailEnableStatus(
          Boolean(data?.euq_send_confirmation_mail?.isEnabled)
        );
        setCustomizedMailEnabled(
          data?.euq_send_confirmation_mail?.confirmationMailConfig
            ?.customizedMail ?? 0
        );
        setCustomizedSubject(
          data?.euq_send_confirmation_mail?.confirmationMailConfig?.subject ??
            ''
        );
        setMessageContent(
          data?.euq_send_confirmation_mail?.confirmationMailConfig
            ?.mailContent ?? ''
        );
        setAiChatEnableStatus(Boolean(data.outlook_addin_ai_chat?.isEnabled));
      } else {
        setIsShowMultiService(false);
      }
    } catch (error: any) {
      setErrorMsg(t('common.apiException.internalError'));
      setUpdateIndex(new Set());
      setUpdateActionIndex(new Set());
      setIsShowMultiService(false);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (orgId === 'all' || !orgId) {
      switchOrgId(groupCompanyGuid);
    }
    (async function doCall() {
      await loadData();
    })();
  }, [addInOrgId]);
  const columns: ColumnDef<EuqSetDataList>[] = useMemo(
    () => [
      {
        id: 'type',
        header: t('administration.addIn.quarantineManagement.reason'),
        accessorKey: 'type',
        size: 300,
        cell: ({ row }) => {
          const { type } = row.original;
          const info: string[] = [
            'Spam',
            'Blocked',
            'UntestedOrUnscannable',
            'Suspicious Object',
          ];
          if (info.indexOf(type) !== -1) {
            return (
              <Flex>
                <Text ml={5}>
                  {t(`administration.addIn.quarantineReason.${type}`)}
                </Text>
                <Info
                  iconStyle={{ ml: '2x', mt: '2px' }}
                  content={t(
                    `administration.addIn.quarantineReason.tooltip.${type}`
                  )}
                />
              </Flex>
            );
          } else {
            return (
              <Text ml={5}>
                {t(`administration.addIn.quarantineReason.${type}`)}
              </Text>
            );
          }
        },
      },
      {
        id: 'view',
        header: ({ table }) => {
          const _viewList = JSON.parse(JSON.stringify(enableViewList));
          updateIndex.forEach((index) => {
            _viewList[items[index].type].view = Number(!items[index].view);
          });
          const uncheckedCount = Object.values(_viewList).filter(
            (item: any) => item.view === 0
          ).length;
          const getIsAllRowsSelected = uncheckedCount !== 0;
          table.getIsAllRowsSelected = function () {
            return getIsAllRowsSelected;
          };
          return (
            <Flex alignItems="center">
              <Checkbox
                checked={!getIsAllRowsSelected}
                indeterminate={
                  uncheckedCount === table.options.data.length
                    ? false
                    : getIsAllRowsSelected
                }
                disabled={isDisabled}
                onChange={() => {
                  const copySet = new Set(updateIndex);
                  if (!getIsAllRowsSelected) {
                    for (const item in items) {
                      if (updateIndex.has(Number(item))) {
                        copySet.delete(Number(item));
                        setUpdateIndex(copySet);
                      } else {
                        copySet.add(Number(item));
                        setUpdateIndex(copySet);
                      }
                    }
                  } else {
                    for (const item in items) {
                      if (items[item].view === 0) {
                        copySet.add(Number(item));
                        setUpdateIndex(copySet);
                      } else {
                        copySet.delete(Number(item));
                        setUpdateIndex(copySet);
                      }
                    }
                  }
                }}
              />
              <Text ml={5}>
                {t('administration.addIn.quarantineManagement.view')}
              </Text>
            </Flex>
          );
        },
        accessorKey: 'view',
        cell: ({ row }) => {
          const { view } = row.original;
          const checked = updateIndex.has(row.index) ? !view : view;
          return (
            <Checkbox
              checked={!!checked}
              disabled={isDisabled}
              onChange={() => {
                const copySet = new Set(updateIndex);
                if (updateIndex.has(row.index)) {
                  copySet.delete(row.index);
                  setUpdateIndex(copySet);
                } else {
                  copySet.add(row.index);
                  setUpdateIndex(copySet);
                }
              }}
            />
          );
        },
        size: 300,
      },
      {
        id: 'take_action',
        header: ({ table }) => {
          const _viewList = JSON.parse(JSON.stringify(enableViewList));
          updateActionIndex.forEach((index) => {
            _viewList[items[index].type].take_action = Number(
              !items[index].take_action
            );
          });
          const uncheckedActionCount = Object.values(_viewList).filter(
            (item: any) => item.take_action === 0
          ).length;
          const getIsAllActionRowsSelected = uncheckedActionCount !== 0;
          return (
            <Flex alignItems="center">
              <Checkbox
                checked={!getIsAllActionRowsSelected}
                indeterminate={
                  uncheckedActionCount === table.options.data.length
                    ? false
                    : getIsAllActionRowsSelected
                }
                disabled={isDisabled || table.getIsAllRowsSelected()}
                onChange={() => {
                  const copySet = new Set(updateActionIndex);
                  if (!getIsAllActionRowsSelected) {
                    for (const item in items) {
                      if (updateActionIndex.has(Number(item))) {
                        copySet.delete(Number(item));
                        setUpdateActionIndex(copySet);
                      } else {
                        copySet.add(Number(item));
                        setUpdateActionIndex(copySet);
                      }
                    }
                  } else {
                    for (const item in items) {
                      if (items[item].take_action === 0) {
                        copySet.add(Number(item));
                        setUpdateActionIndex(copySet);
                      } else {
                        copySet.delete(Number(item));
                        setUpdateActionIndex(copySet);
                      }
                    }
                  }
                }}
              />
              <Text ml={5}>
                {t('administration.addIn.quarantineManagement.restore')}
              </Text>
            </Flex>
          );
        },
        cell: ({ row }) => {
          const { take_action, view } = row.original;
          const actionChecked = updateActionIndex.has(row.index)
            ? !take_action
            : take_action;
          return (
            <Checkbox
              checked={!!actionChecked}
              disabled={
                !!((updateIndex.has(row.index) ? view : !view) || isDisabled)
              }
              onChange={() => {
                const copySet = new Set(updateActionIndex);
                if (updateActionIndex.has(row.index)) {
                  copySet.delete(row.index);
                  setUpdateActionIndex(copySet);
                } else {
                  copySet.add(row.index);
                  setUpdateActionIndex(copySet);
                }
              }}
            />
          );
        },
        size: 300,
      },
    ],
    [items, updateIndex, updateActionIndex, enableStatus, isDisabled]
  );
  const table = useReactTable({
    data: items,
    columns,
    defaultColumn: {
      minSize: 40,
    },
    getCoreRowModel: getCoreRowModel(),
    state: {
      rowSelection,
    },
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
  });
  const cellHeight = 36;
  const headerHeight = 36;
  const rowsToDisplay = 13;
  const tableStyleProps = {
    height: headerHeight + rowsToDisplay * cellHeight,
  };
  const { widthEditablePermission } = usePermissionStore();
  const onSave = () => {
    const _viewList = JSON.parse(JSON.stringify(enableViewList));
    updateIndex.forEach((index) => {
      _viewList[items[index].type].view = Number(!items[index].view);
    });
    if (
      _viewList['Ransomware'].view ||
      _viewList['Malicious File'].view ||
      _viewList['Malicious URL'].view
    ) {
      setSaveModalTitle(t('administration.addIn.confirm.save.title'));
      setSaveModalContent(t('administration.addIn.confirm.save.content'));
      onOpen();
    } else {
      confirmSave();
    }
  };
  const confirmSave = async () => {
    setSaveLoading(true);
    const threatTypeList = JSON.parse(JSON.stringify(enableViewList));
    updateIndex.forEach((index) => {
      threatTypeList[items[index].type].view = Number(!items[index].view);
    });
    updateActionIndex.forEach((index) => {
      threatTypeList[items[index].type].take_action = Number(
        !items[index].take_action
      );
    });
    const params: EuqSettingsList = {
      euq_assistant: {
        approved_sender: {
          enabled: Number(approvedSenderEnable),
        },
        isEnabled: Number(enableStatus),
        threat_type: threatTypeList,
      },
      euq_feedback: {
        isEnabled: Number(feedbackEnableStatus),
      },
      euq_feedback_manual_approve: {
        isEnabled: Number(feedbackManualEnableStatus),
      },
      euq_feedback_skip_on_approved_header: {
        isEnabled: Number(feedbackSkipOnApprovedHeaderEnableStatus),
      },
      euq_feedback_to_admin: {
        isEnabled: Number(feedbackToAdminEnableStatus),
        euq_feedback_mail_boxes: feedbackToAdminMailBoxes,
      },
      euq_move_to_junk_folder: {
        isEnabled: Number(moveToJunkFolderEnableStatus),
      },
      euq_send_confirmation_mail: {
        isEnabled: Number(confirmationMailEnableStatus),
        confirmationMailConfig: {
          customizedMail: customizedMailEnabled,
          subject: customizedSubject,
          mailContent: messageContent,
        },
      },
      euq_feedback_phishing_spam_auto_remediation: {
        isEnabled: Number(feedbackPhishingSpamAutoRemediationEnableStatus),
      },
      outlook_addin_ai_chat: {
        isEnabled: Number(aiChatEnableStatus),
      },
    };
    try {
      const res = await AdministrationApi.updateEuqSettings(
        params,
        apiCategory
      );
      if (res.data) {
        window.showNotification({
          titleMsg: t('globalSetting.switch.success'),
          severity: 'success',
        });
      } else {
        window.showNotification({
          titleMsg: t('common.apiException.internalError'),
          severity: 'error',
        });
      }
    } catch (error: any) {
      if (
        error?.status === 400 &&
        error?.data?.error?.code === 'InvalidInput'
      ) {
        const detail = error.data.error.details?.[0];
        window.showNotification({
          titleMsg: t('administration.addIn.saveError.title'),
          bodyMsg: detail?.message || t('common.apiException.internalError'),
          severity: 'error',
        });
        return;
      } else if (
        error?.status === 404 &&
        error?.data.error.details[0].message ===
          'not find this mail in your org'
      ) {
        window.showNotification({
          titleMsg: t('administration.addIn.saveError.invalidInput.title'),
          bodyMsg: t('administration.addIn.saveError.invalidInput.content'),
          severity: 'error',
        });
        return;
      } else {
        window.showNotification({
          titleMsg: t('common.apiException.internalError'),
          severity: 'error',
        });
      }
    } finally {
      onClose();
      setSaveLoading(false);
      await loadData();
    }
  };
  return (
    <Flex flexDirection="column" flex={1} minHeight={0}>
      <Flex flexDirection="column" flex={1} overflow="auto">
        {loading && <CSpinner />}
        <Stack gap="4x" m="4x">
          {errorMsg && (
            <ErrorAlert
              errorMsg={errorMsg}
              onClose={() => setErrorMsg(undefined)}
            />
          )}
          <SaveModal
            visible={isOpen}
            onClose={onClose}
            onConfirm={confirmSave}
            loading={saveLoading}
            title={saveModalTitle}
            content={saveModalContent}
          />
          {descriptionContent}
          <Flex alignItems="center" columnGap="4x">
            <Text color={colorStyle.color.secondary}>
              {t('common.label.currentOrganization')}:
            </Text>
            <Organization showLabel={false} allOrgItem={false} />
          </Flex>
          <Divider width="100%" />
          <Text size="md">
            {t('administration.addIn.quarantineManagement.title')}
          </Text>
          {/* Only show the consumer-provided banner once the service is known
              to be provisioned; otherwise it leaks into the loading and
              not-provisioned states (where the notProvision message shows). */}
          {isShowMultiService && !loading && infoBanner}
        </Stack>
        {isShowMultiService && !loading ? (
          <>
            <Flex mb="4x" mx="4x">
              <Text color={colorStyle.color.secondary}>
                {t('administration.addIn.quarantineManagement.content')}
              </Text>
            </Flex>
            <Flex alignItems="center" m="16px">
              <Text>{t('administration.addIn.status')}:</Text>
              <Switch
                mr="1x"
                ml="10px"
                id="addInStatus"
                checked={enableStatus}
                disabled={forceDisable}
                onChange={(e: any) => {
                  setEnableStatus(e.target.checked);
                }}
              />
            </Flex>
            <Flex m="16px">
              <Table layout="flexbox" {...tableStyleProps}>
                <TableHeader>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => {
                        const styleProps = {
                          minWidth: header.column.columnDef.minSize,
                          width: header.getSize(),
                        };
                        return (
                          <TableCell key={header.id} {...styleProps}>
                            {header.isPlaceholder ? null : (
                              <SmartTooltip>
                                {flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                              </SmartTooltip>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody overflowY="scroll">
                  {table.getRowModel().rows.map((row) => (
                    <TableRow key={row.id}>
                      {row.getVisibleCells().map((cell) => {
                        const styleProps = {
                          minWidth: cell.column.columnDef.minSize,
                          width: cell.column.getSize(),
                        };
                        return (
                          <TableCell key={cell.id} {...styleProps}>
                            <SmartTooltip>
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </SmartTooltip>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Flex>
            <Flex alignItems="center" m="25px 16px">
              <Text>
                {t('administration.addIn.quarantineManagement.trustSender')}:
              </Text>
              <Space width="2x"></Space>
              <Checkbox
                mt={2}
                value={Number(approvedSenderEnable)}
                checked={approvedSenderEnable}
                disabled={isDisabled}
                onChange={(e: any) => {
                  setApprovedSenderEnable(e.target.checked);
                }}
              >
                {t(
                  'administration.addIn.quarantineManagement.trustSender.content'
                )}
              </Checkbox>
            </Flex>
          </>
        ) : null}
        {!isShowMultiService && !loading ? (
          <Flex m="16px">
            <Text color={colorStyle.color.secondary}>{t(notProvisionKey)}</Text>
          </Flex>
        ) : null}
        {!IS_MEA && policyTypes.includes(PolicyType.Exchange) && (
          <>
            <Divider mx="4x" />
            <Flex flexDirection="column" m="16px">
              <Text size="md">{t('administration.addIn.aiChat.title')}</Text>
              <Text color={colorStyle.color.secondary} mt="2x">
                {t('administration.addIn.aiChat.content')}
              </Text>
              <Flex alignItems="center" mt="3x">
                <Switch
                  id="aiChatStatus"
                  checked={aiChatEnableStatus}
                  onChange={(e: any) => {
                    setAiChatEnableStatus(e.target.checked);
                  }}
                />
                <Text ml="10px">
                  {t('administration.addIn.aiChat.switchLabel')}
                </Text>
                <Tag variant="outline" size="sm" ml="2x">
                  {t('common.label.preview')}
                </Tag>
              </Flex>
              <Text
                color={colorStyle.color.secondary}
                mt="1x"
                ml="56px"
                fontSize="sm"
              >
                {t('administration.addIn.aiChat.description')}
              </Text>
            </Flex>
          </>
        )}
        <Divider mx="4x" />
        <Text color={colorStyle.color.secondary} m="4x">
          {t('administration.addIn.emailReporting.goTo')}{' '}
          <LinkButton
            display="inline-block"
            onClick={() => {
              const path = `/administrator/email-reporting`;
              handleNavigate(path);
              navigate(path);
            }}
          >
            {t('administration.title')}
            {' > '}
            {t('administration.addIn.emailReporting.title')}
          </LinkButton>
        </Text>
      </Flex>
      <Flex flexDirection="column" flexShrink={0}>
        {isShowMultiService && !loading ? (
          <Flex
            px="5x"
            background={colorStyle.background.secondary}
            height={64}
            width="100%"
            align="center"
            justify="flex-start"
          >
            <Button
              variant="primary"
              onClick={onSave}
              disabled={
                forceDisable ||
                !widthEditablePermission(PermissionNameEnum.administrator) ||
                (feedbackToAdminEnableStatus &&
                  feedbackToAdminMailBoxes.length === 0)
              }
            >
              {t('common.action.label.save')}
            </Button>
          </Flex>
        ) : null}
      </Flex>
    </Flex>
  );
};

export default AddInPanel;
